// lib
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { DATA_LAB_HELP, DATA_LAB_BUTTON_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'
import { DATA_LAB_FEATURES } from '@aytm/app/stats/page/sidebar/components/lib/constants'

import { GetDataByKey } from '@aytm/lib/storage/index.js'
import { isReportStorageDisabled } from '@aytm/app/stats/lib/stat-page'

// components
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'
import DropTargetContainer from '@aytm/app/stats/page/sidebar/components/questions/DropTargetContainer'
import CustomQuestionButton from '@aytm/app/stats/page/sidebar/components/questions/CustomQuestionButton'
import PinnedCustomQuestion from '@aytm/app/stats/page/sidebar/components/questions/PinnedCustomQuestion'

// styles
import style from '@aytm/app/stats/page/sidebar/data-lab/styles/DataLabPanel.module.scss'

const DataLabPanel = ({
  sidebar,
  model,
  features,
  addQuestion,
  addPinnedQuestion,
  removePinnedQuestion,
  onModeChange,
  onDNDStart,
  onDNDEnd
}) => {
  const { dataLabButtons: buttons } = model

  const disabled =
    'charts' != model.mode || model.isReportVirtual || model.isReportXpert || features.reorder_questions.disabled

  const [questions, setQuestions] = useState([])

  const componentRef = useRef(null)

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  // Where the report cannot be stored nothing can be pinned, so no pin drop is offered at all
  const pinDisabled = isReportStorageDisabled()
  const acceptTypes = useMemo(
    () =>
      pinDisabled
        ? []
        : buttons.reduce((acc, cur) => {
            if (!features[cur.feature].disabled) acc.push(cur.adndType)
            return acc
          }, []),
    [buttons, features, pinDisabled]
  )

  const typesTitles = useMemo(
    () =>
      buttons.reduce((acc, cur) => {
        if (!features[cur.feature].disabled) acc[cur.adndType] = cur.text
        return acc
      }, {}),
    [buttons, features]
  )

  const getLocalStorageData = useCallback(
    () =>
      GetDataByKey('AYTM-Reports', `${PageGlobals.SURVEY_ID}${PageGlobals.USER_ID || 0}pquestions`).then(data =>
        setQuestions(
          null !== data
            ? data.reduce((acc, cur, index) => {
                if (
                  !['dlv', 'dlc', 'dlx', 'dld', 'sn'].includes(cur.type) ||
                  (Object.hasOwn(cur, 'features') && cur.features.insightsMapping)
                ) {
                  return acc
                }
                if (features.data_lab_vq.disabled && 'dlv' === cur.type) {
                  return acc
                }
                if (features.data_lab_cf.disabled && 'dlc' === cur.type) {
                  return acc
                }
                if (features.data_lab_xpert.disabled && 'dlx' === cur.type) {
                  return acc
                }
                if (features.data_lab_concept.disabled && 'dld' === cur.type) {
                  return acc
                }
                if (features.data_lab_storytelling.disabled && 'sn' === cur.type) {
                  return acc
                }
                const data = JSON.parse(JSON.stringify(cur))
                delete data.features
                delete data.variables
                acc.push({
                  type: cur.type,
                  text: cur.text,
                  icon: PageGlobals.QUESTION_TYPE_ICONS[cur.type],
                  data: data,
                  index: index
                })
                return acc
              }, [])
            : []
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    DATA_LAB_FEATURES.map(feature => features[feature].disabled)
  )

  useEffect(() => getLocalStorageData(), [getLocalStorageData])

  useEventListener('pquestionsLocalChangesChanged', () => getLocalStorageData())

  return (
    <>
      <SidebarPopupWarning
        features={
          // reorder_questions.disabled folds in mode/report/Xpert/QA, so its own status decides the message
          disabled
            ? [features['reorder_questions']]
            : DATA_LAB_FEATURES.map(feature => features[feature]).filter(item => !item.hidden)
        }
        isSpecial={'charts' != model.mode}
        isReportVirtual={model.isReportVirtual}
        isReportXpert={model.isReportXpert}
        onModeChange={onModeChange}
      />

      <div ref={setComponentRef} className={style.component}>
        <div className={style['component-title']}>
          Data Lab
          {root && <SidebarPopup id="datalab" root={root} help={DATA_LAB_HELP} width="300px" />}
        </div>

        <DropTargetContainer
          root={sidebar}
          acceptTypes={acceptTypes}
          typesTitles={typesTitles}
          onDNDDrop={addPinnedQuestion}
        >
          <div className={style.section}>
            {buttons.map(item =>
              features[item.feature].hidden ? null : (
                <CustomQuestionButton
                  dataTestID={`stat-data-lab-${item.id}`}
                  key={item.type}
                  root={root}
                  item={{ ...item, help: DATA_LAB_BUTTON_HELP[item.id] || '' }}
                  features={features}
                  disabled={features[item.feature].disabled || disabled}
                  addQuestion={addQuestion}
                  onDNDStart={onDNDStart}
                  onDNDEnd={onDNDEnd}
                />
              )
            )}

            <div className={`${style.row} ${style.note}`}>
              Click the button or drag it to the right to add your new chart exactly where you want it.
            </div>
          </div>

          {0 < questions.length && !disabled ? (
            <div className={style.section}>
              <div className={style['section-title']}>Pinned questions</div>

              {questions.map(item => (
                <PinnedCustomQuestion
                  key={`question-${item.index}`}
                  item={item}
                  addQuestion={addQuestion}
                  removePinnedQuestion={removePinnedQuestion}
                  onDNDStart={onDNDStart}
                  onDNDEnd={onDNDEnd}
                />
              ))}
            </div>
          ) : null}
        </DropTargetContainer>
      </div>
    </>
  )
}

DataLabPanel.propTypes = {
  sidebar: PropTypes.any,
  root: PropTypes.any,
  model: PropTypes.object,
  features: PropTypes.object,
  questions: PropTypes.array,
  addQuestion: PropTypes.func,
  addPinnedQuestion: PropTypes.func,
  removePinnedQuestion: PropTypes.func,
  onModeChange: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func
}

DataLabPanel.displayName = 'DataLabPanel'

export default DataLabPanel
